/**
 * Services component for Manatee Labs website.
 * Follows the design specifications from manatee-labs-full-spec.md
 */
import { useState } from "react";
import { motion } from "framer-motion";
import "./services.css";
import { Server, Brain, Database, BarChart } from "lucide-react";

interface Service {
  id: string;
  title: string;
  description: string;
  icon: string;
}

interface ServicesData {
  headline: string;
  subheadline: string;
  services: Service[];
  ctaText: string;
}

export const Services = () => {
  const [servicesData] = useState<ServicesData>({
    headline: "Our Services",
    subheadline:
      "From implementation to real-time intelligence, we help businesses get the most out of Amazon Connect and AWS.",
    services: [
      {
        id: "connect-setup",
        title: "Amazon Connect Setup & Optimization",
        description:
          "End-to-end consulting for businesses deploying Amazon Connect.",
        icon: "server",
      },
      {
        id: "ai-routing",
        title: "AI-Powered Call Routing Solutions",
        description:
          "Advanced ML models to dynamically route calls for improved customer experience.",
        icon: "brain",
      },
      {
        id: "custom-integrations",
        title: "Custom Integrations & Enhancements",
        description:
          "Integrate Amazon Connect with your CRM, ERP, and workforce management tools.",
        icon: "database",
      },
      {
        id: "big-data",
        title: "Big Data Analysis & Insight Generation",
        description:
          "Combine data from Amazon Connect, CRM systems, and other sources into unified dashboards using QuickSight and ML—built with robust AWS-native pipelines for real-time insights.",
        icon: "chart",
      },
    ],
    ctaText: "Schedule a Consultation",
  });

  // Animation variants for fade-in effect
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  // Render icon based on icon name
  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case "server":
        return <Server size={48} color="#173fa3" />;
      case "brain":
        return <Brain size={48} color="#173fa3" />;
      case "database":
        return <Database size={48} color="#173fa3" />;
      case "chart":
        return <BarChart size={48} color="#173fa3" />;
      default:
        return null;
    }
  };

  return (
    <div id="services">
      <div className="container">
        <motion.div
          className="services-header"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
        >
          <h2 className="services-title">{servicesData.headline}</h2>
          <p className="services-subtitle">{servicesData.subheadline}</p>
        </motion.div>

        <div className="services-grid">
          {servicesData.services.map((service, index) => (
            <motion.div
              className="service-card"
              key={service.id}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{
                hidden: { opacity: 0, y: 20 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: {
                    duration: 0.6,
                    delay: index * 0.2,
                  },
                },
              }}
              whileHover={{
                y: -10,
                boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
              }}
            >
              <div className="service-icon">{renderIcon(service.icon)}</div>
              <div className="service-content">
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">{service.description}</p>
                {/* <motion.a
                  href={`#contact`}
                  className="service-cta"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Learn More
                </motion.a> */}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="cta-section"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={{
            hidden: { opacity: 0, y: 20 },
            visible: {
              opacity: 1,
              y: 0,
              transition: {
                duration: 0.6,
                delay: 0.6,
              },
            },
          }}
        >
          <motion.a
            href="#contact"
            className="cta-button"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {servicesData.ctaText}
          </motion.a>
        </motion.div>
      </div>
    </div>
  );
};
